import { theme } from 'antd';
import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { Notification } from 'zComponents/atoms';
import { Button, Col, Form, Input, Modal, Row, Select } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { TextArea } = Input;

const EditAssetGroupModal = ({ children, data: group }) => {
	const {
		token: { colorTextDescription }
	} = theme.useToken();

	// Hooks
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	// Local States
	const [updateGroupLoading, setUpdateGroupLoading] = useState(false);
	const [saveDisable, setSaveDisable] = useState(true);
	const [visible, setVisible] = useState(false);

	// Global State
	const { data: sitesData, loading: sitesPending } = useSelector((state) => state.Sites);

	const handleCancel = () => {
		setVisible(false);
	};

	const handleSubmit = () => {
		setUpdateGroupLoading(true);
		const inputs = removeEmptyOrNull(form.getFieldsValue(true));
		const body = {
			name: inputs.name,
			description: inputs.description,
			siteID: inputs.siteID
		};
		SuperFetch.patch('/deviceGroups/' + group.groupID, body)
			.then((res) => {
				if (res.statusCode === 200) {
					Notification('success', `Successfully Updated ${body.name}.`);
					dispatch(assetGroupActions.loadGroups());
					handleCancel();
				} else if (res.error.statusCode === 400) {
					Notification('error', 'Please fill out all required information.');
				} else {
					Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
				}
				setUpdateGroupLoading(false);
			})
			.catch((e) => {
				Notification('error', 'Something went wrong, try again soon.');
				setUpdateGroupLoading(false);
				handleCancel();
			});
	};
	const updateChange = () => {
		const formData = form.getFieldsValue(true);
		return (
			(group.name === formData.name || formData.name === '') &&
			(group.description === formData.description || formData.description === '') &&
			group.siteID === formData.siteID
		);
	};

	const isPermittedUpdateGroup = useManagePermittedRoles('updateGroup', {
		orgID: [group.orgID],
		siteID: [group.siteID],
		floorID: []
	});

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={'Modify Group'}
				condition={isPermittedUpdateGroup}
				disabledCondition={!isPermittedUpdateGroup}
				trueTitle={'Edit this Group’s Information'}
				falseTitle={"You don't have permission"}
				onClick={() => setVisible(true)}
				size={'small'}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				title={'Update ' + group.name}
				open={visible}
				onCancel={handleCancel}
				destroyOnClose={true}
				width={600}
				footer={[
					<Button key='back' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						form={form}
						key='submit'
						htmlType='submit'
						type='primary'
						disabled={saveDisable}
						loading={updateGroupLoading}
					>
						Update
					</Button>
				]}
			>
				<Form
					form={form}
					id={form}
					layout='vertical'
					onFinish={handleSubmit}
					initialValues={group}
					autoComplete='off'
					onValuesChange={() => setSaveDisable(updateChange())}
				>
					<Row gutter={16}>
						<Col sm={24} xs={24} lg={12} md={12}>
							<Form.Item name='name' label='Name' rules={fieldsValidator.Name}>
								<Input />
							</Form.Item>
						</Col>
						<Col sm={24} xs={24} lg={12} md={12}>
							<Form.Item name='siteID' label='Site'>
								<Select
									placeholder={'Select Site'}
									loading={sitesPending}
									options={sitesData.map((site) => ({ label: site.name, value: site.siteID }))}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item label='Description' name='description' rules={fieldsValidator.Description}>
								<TextArea
									style={{ color: colorTextDescription }}
									allowClear
									autoSize={{ minRows: 2, maxRows: 6 }}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default EditAssetGroupModal;
